/**
 * Register or log in with a 6-digit email code, Google or Facebook (designs/Caged Home: the
 * sign-in sheet; T6, D21, D28). Register links the email to this device's guest account, so
 * progress stays; then "Complete signup" asks for the country (suggested, L8) and the 18+
 * confirmation (O7, A1 in designs/README.md). Log in switches this device to an existing account.
 * Google and Facebook link the guest's account when the identity is new, and log in to the
 * account it belongs to otherwise (net/social.ts); the sheet opens again when they send the
 * player back, to finish.
 */
import { playableFighter } from '@caged/shared';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { AuthMode, AuthProblem } from '../../net/account';
import { connectionCountry } from '../../net/geo';
import { finishSocial, socialProviders, startSocial, type SocialProvider } from '../../net/social';
import { diag } from '../../platform/diag';
import { useApp } from '../../state/store';
import { countryNames } from '../countries';
import { Modal } from './Modal';
import s from './AuthSheet.module.css';
import ui from './ui.module.css';

/** Seconds before a code can be sent again. */
const RESEND_SECONDS = 30;
export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'email' | 'code' | 'signup' | 'finishing';

/** Google's "G" (brand guidelines: the logo as it is, on white). */
export function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export const PROVIDER_NAMES: Record<SocialProvider, string> = { google: 'Google', facebook: 'Facebook' };

export function AuthSheet({ initialMode, onClose }: { initialMode: AuthMode | 'signup'; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const [mode, setMode] = useState<AuthMode>(initialMode === 'signup' ? 'register' : initialMode);
  // Back from Google or Facebook: straight to finishing (below). A registered player who never
  // finished signup (a league needs their country) goes straight to it.
  const [step, setStep] = useState<Step>(() => (useApp.getState().socialReturn ? 'finishing' : initialMode === 'signup' ? 'signup' : 'email'));
  // Registering from the post-fight offer (D41) brings the email along: its code goes out at once.
  const [email, setEmail] = useState(() => useApp.getState().authEmail ?? '');
  const [code, setCode] = useState('');
  const [codeFocus, setCodeFocus] = useState(false);
  const [problem, setProblem] = useState<AuthProblem | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [country, setCountry] = useState('');
  const [adult, setAdult] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const accountChanged = useApp((st) => st.accountChanged);
  const socialReturn = useApp((st) => st.socialReturn);
  const [providers, setProviders] = useState<Record<SocialProvider, boolean>>({ google: false, facebook: false });
  const finishing = useRef(false);

  useEffect(() => {
    void socialProviders().then(setProviders);
  }, []);

  // Back from Google or Facebook: finish signing in, then "Complete signup" for a new account.
  useEffect(() => {
    if (!socialReturn || finishing.current) return;
    finishing.current = true;
    setStep('finishing');
    void (async () => {
      try {
        const outcome = await finishSocial(socialReturn);
        if (outcome === 'redirecting') return;
        useApp.setState({ socialReturn: null });
        if (outcome === 'cancelled') return setStep('email');
        // Signed in to an account that already had a fighter: its name and fighter come along.
        await accountChanged();
        const { fetchProfileRemote } = await import('../../net/progressApi');
        const profile = await fetchProfileRemote().catch(() => null);
        if (outcome === 'linked' || (profile && !profile.signupDone)) setStep('signup');
        else onClose();
      } catch (err) {
        diag.error(`auth: ${String(err)}`);
        useApp.setState({ socialReturn: null });
        setProblem('socialFailed');
        setStep('email');
      }
    })();
  }, [socialReturn, accountChanged, onClose]);

  const social = async (provider: SocialProvider) => {
    setBusy(true);
    setProblem(null);
    try {
      // Leaves for the provider's page; the game opens again when it sends the player back.
      await startSocial(provider, mode);
    } catch (err) {
      diag.error(`auth: ${String(err)}`);
      setProblem('socialFailed');
      setBusy(false);
    }
  };

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  // Signup suggests the country Cloudflare sees (L8); the player can change it.
  useEffect(() => {
    if (step !== 'signup' || country) return;
    void connectionCountry().then((geo) => {
      if (geo) setCountry((c) => c || geo);
    });
  }, [step, country]);

  const countries = useMemo(() => countryNames(i18n.language), [i18n.language]);

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const address = email.trim();
    if (!EMAIL.test(address)) return setProblem('badEmail');
    setBusy(true);
    setProblem(null);
    try {
      const { sendCode } = await import('../../net/account');
      await sendCode(mode, address);
      setStep('code');
      setCode('');
      setResendIn(RESEND_SECONDS);
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (err) {
      const problem = (err as { problem?: AuthProblem }).problem ?? 'offline';
      setProblem(problem);
      // Already signed in to an account: the screens behind the sheet catch up with who this is.
      if (problem === 'signedIn') void accountChanged();
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e?: FormEvent) => {
    e?.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const { verifyCode } = await import('../../net/account');
      await verifyCode(mode, email.trim(), code);
      // Log in: the account's progress, name and fighter replace this device's.
      await accountChanged();
      if (mode === 'register') setStep('signup');
      else onClose();
    } catch (err) {
      setProblem((err as { problem?: AuthProblem }).problem ?? 'offline');
    } finally {
      setBusy(false);
    }
  };

  const finish = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!country || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const { completeSignupRemote } = await import('../../net/progressApi');
      // The name and fighter only start an account that never fought; one that did keeps its own.
      const { settings, progress } = useApp.getState();
      await completeSignupRemote({ country, adult, name: settings.playerName || 'Fighter', fighterId: playableFighter(progress, settings.fighterId) });
      await accountChanged();
      onClose();
    } catch {
      setProblem('offline');
    } finally {
      setBusy(false);
    }
  };

  const sentFirst = useRef(false);
  useEffect(() => {
    if (sentFirst.current || step !== 'email' || !useApp.getState().authEmail) return;
    sentFirst.current = true;
    void send();
    // Once, with the email the sheet opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchMode = (m: AuthMode) => {
    setMode(m);
    setStep('email');
    setProblem(null);
  };

  const error = problem ? (
    <p className={s.error} role="alert">
      {t(`screens.auth.errors.${problem}`)}
    </p>
  ) : null;

  if (step === 'finishing') {
    return (
      <Modal onClose={onClose} labelledBy={titleId} variant="sheet">
        <div className={s.sheet} aria-busy="true">
          <h2 id={titleId} className={s.title}>
            {t('screens.auth.finishing')}
          </h2>
          <div className={s.working} aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </div>
      </Modal>
    );
  }

  if (step === 'signup') {
    return (
      <Modal onClose={onClose} labelledBy={titleId} variant="sheet">
        <form className={s.sheet} onSubmit={(e) => void finish(e)}>
          <h2 id={titleId} className={s.title}>
            {t('screens.auth.signupTitle')}
          </h2>
          <p className={s.sub}>{t('screens.auth.signupSub')}</p>
          <label className={s.field}>
            <span className={ui.label}>{t('screens.auth.country')}</span>
            <select className={s.input} value={country} onChange={(e) => setCountry(e.target.value)} required>
              <option value="" disabled>
                —
              </option>
              {countries.sorted.map((c) => (
                <option key={c} value={c}>
                  {countries.name(c)}
                </option>
              ))}
            </select>
          </label>
          <label className={s.check}>
            <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} />
            <span className={s.checkText}>
              <span className={s.checkLabel}>{t('screens.auth.adult')}</span>
              <span className={s.hint}>{t('screens.auth.adultHint')}</span>
            </span>
          </label>
          {error}
          <button type="submit" className={[ui.primary, s.cta].join(' ')} disabled={!country || busy}>
            {t('screens.auth.done')}
          </button>
        </form>
      </Modal>
    );
  }

  const full = code.length === 6;
  return (
    <Modal onClose={onClose} labelledBy={titleId} variant="sheet">
      <div className={s.sheet}>
        <div className={s.tabs} role="tablist">
          {(['login', 'register'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              className={[s.tab, mode === m ? s.tabOn : ''].join(' ')}
              onClick={() => switchMode(m)}
            >
              {t(`screens.auth.${m}`)}
            </button>
          ))}
        </div>
        <h2 id={titleId} className={s.title}>
          {t(mode === 'register' ? 'screens.auth.registerTitle' : 'screens.auth.loginTitle')}
        </h2>
        <p className={s.sub}>{t(mode === 'register' ? 'screens.auth.registerSub' : 'screens.auth.loginSub')}</p>

        {step === 'email' ? (
          <form className={s.form} onSubmit={(e) => void send(e)} noValidate>
            <input
              className={s.input}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('screens.auth.email')}
              aria-label={t('screens.auth.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            {mode === 'login' && <p className={s.hint}>{t('screens.auth.loginNote')}</p>}
            {error}
            <button type="submit" className={[ui.primary, s.cta].join(' ')} disabled={busy}>
              {t('screens.auth.sendCode')}
            </button>
          </form>
        ) : (
          <form className={s.form} onSubmit={(e) => void verify(e)}>
            <div className={s.sent}>
              <span className={s.sentLabel}>{t('screens.auth.codeSent')}</span>
              <span className={s.sentText}>
                <Trans i18nKey="screens.auth.enterCode" values={{ email: email.trim() }} components={{ g: <span className={s.sentTo} /> }} />
              </span>
            </div>
            <label className={s.boxes}>
              {Array.from({ length: 6 }, (_, i) => (
                <span
                  key={i}
                  className={[s.box, codeFocus && (i === code.length || (full && i === 5)) ? s.boxOn : '', code[i] ? s.boxFilled : ''].join(' ')}
                  aria-hidden="true"
                >
                  {code[i] ?? ''}
                  {codeFocus && i === code.length && <span className={s.caret} />}
                </span>
              ))}
              <input
                ref={codeRef}
                className={s.codeInput}
                aria-label={t('screens.auth.codeLabel')}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onFocus={() => setCodeFocus(true)}
                onBlur={() => setCodeFocus(false)}
              />
            </label>
            {error}
            <button type="submit" className={[ui.primary, s.cta].join(' ')} disabled={!full || busy}>
              {t('screens.auth.verify')}
            </button>
            <div className={s.links}>
              <button type="button" className={s.link} onClick={() => switchMode(mode)}>
                {t('screens.auth.otherEmail')}
              </button>
              <button type="button" className={[s.link, s.linkStrong].join(' ')} disabled={resendIn > 0 || busy} onClick={() => void send()}>
                {resendIn > 0 ? t('screens.auth.resendIn', { seconds: resendIn }) : t('screens.auth.resend')}
              </button>
            </div>
          </form>
        )}

        {(providers.google || providers.facebook) && (
          <>
            <div className={s.or}>{t('screens.auth.or')}</div>
            <div className={s.social}>
              {(['google', 'facebook'] as const)
                .filter((p) => providers[p])
                .map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={[s.provider, p === 'google' ? s.google : s.facebook].join(' ')}
                    aria-label={t('screens.auth.withProvider', { provider: PROVIDER_NAMES[p] })}
                    disabled={busy}
                    onClick={() => void social(p)}
                  >
                    {p === 'google' ? <GoogleMark /> : <span className={s.fbMark}>f</span>}
                    {PROVIDER_NAMES[p]}
                  </button>
                ))}
            </div>
          </>
        )}
        <button type="button" className={s.guest} onClick={onClose}>
          {t('screens.auth.keepGuest')}
        </button>
        <p className={s.agree}>
          <Trans
            i18nKey="screens.auth.agree"
            components={{
              terms: <a href="/terms" target="_blank" rel="noopener" />,
              privacy: <a href="/privacy" target="_blank" rel="noopener" />,
            }}
          />
        </p>
      </div>
    </Modal>
  );
}
